import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, Play, Pause, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { API } from "@/lib/api";
import { toast } from "sonner";

const ExercisesPage = () => {
  const [exercises, setExercises] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1);
  const [timeLeft, setTimeLeft] = useState(0);
  const [phase, setPhase] = useState("intro");
  const timerRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    API.get("/exercises").then(res => {
      setExercises(res.data);
      setLoading(false);
      const openId = searchParams.get("open");
      if (openId) {
        setSearchParams({}, { replace: true });
        API.get(`/exercises/${openId}`).then(r => {
          setActive(r.data);
          setPhase("intro");
          setCurrentStep(-1);
          setRunning(false);
        }).catch(() => {});
      }
    }).catch(() => setLoading(false));
  }, []);

  const startExercise = async (id) => {
    try {
      const res = await API.get(`/exercises/${id}`);
      setActive(res.data);
      setPhase("intro");
      setCurrentStep(-1);
      setRunning(false);
    } catch { toast.error("Kunde inte ladda övningen"); }
  };

  const beginExercise = () => {
    setPhase("active");
    setCurrentStep(0);
    setTimeLeft(active.steps[0].duration);
    setRunning(true);
  };

  useEffect(() => {
    if (!running || phase !== "active") return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          const nextStep = currentStep + 1;
          if (nextStep >= active.steps.length) {
            setPhase("outro");
            setRunning(false);
            clearInterval(timerRef.current);
            return 0;
          }
          setCurrentStep(nextStep);
          return active.steps[nextStep].duration;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [running, currentStep, phase, active]);

  const togglePause = () => setRunning(!running);

  const resetExercise = () => {
    clearInterval(timerRef.current);
    setPhase("intro");
    setCurrentStep(-1);
    setRunning(false);
    setTimeLeft(0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-[#A0655A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Active exercise view
  if (active) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="pt-24 sm:pt-28 pb-8 sm:pb-20 px-4 sm:px-6">
          <div className="max-w-xl mx-auto">
            <button onClick={() => { resetExercise(); setActive(null); }} className="flex items-center text-[#A0655A] mb-8 hover:underline text-sm" data-testid="back-from-exercise">
              <ChevronLeft size={16} className="mr-1" /> Tillbaka till övningar
            </button>

            <p className="text-sm uppercase tracking-[0.25em] text-[#A0655A] font-medium mb-2">{active.subtitle}</p>
            <h1 className="heading-font text-2xl sm:text-3xl font-medium text-[#111111] mb-2" data-testid="exercise-title">{active.title}</h1>
            <p className="text-sm text-[#111111]/40 mb-10">{active.duration_minutes} minuter</p>

            <AnimatePresence mode="wait">
              {phase === "intro" && (
                <motion.div key="intro" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="text-center">
                  <div className="bg-white border border-[#EBE3DF] rounded-2xl p-8 mb-8">
                    <p className="text-[#111111]/70 leading-relaxed text-base" data-testid="exercise-intro">{active.intro}</p>
                  </div>
                  <Button onClick={beginExercise} className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full px-10 py-6 text-base" data-testid="start-exercise-btn">
                    <Play size={18} className="mr-2" /> Börja
                  </Button>
                </motion.div>
              )}

              {phase === "active" && active.steps[currentStep] && (
                <motion.div key={`step-${currentStep}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="text-center">
                  {/* Breathing circle */}
                  <div className="relative w-48 h-48 mx-auto mb-8">
                    <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="45" fill="none" stroke="#EBE3DF" strokeWidth="3" />
                      <circle cx="50" cy="50" r="45" fill="none" stroke="#A0655A" strokeWidth="3"
                        strokeDasharray={`${(timeLeft / active.steps[currentStep].duration) * 283} 283`}
                        strokeLinecap="round" className="transition-all duration-1000" />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-3xl font-light text-[#111111]" data-testid="exercise-timer">{timeLeft}</span>
                    </div>
                  </div>

                  <p className="heading-font text-xl text-[#111111] mb-8 leading-relaxed" data-testid="exercise-instruction">
                    {active.steps[currentStep].instruction}
                  </p>

                  <p className="text-xs text-[#111111]/30 mb-6">{currentStep + 1} av {active.steps.length}</p>

                  <div className="flex gap-3 justify-center">
                    <Button onClick={togglePause} variant="outline" className="rounded-full" data-testid="pause-exercise-btn">
                      {running ? <Pause size={16} /> : <Play size={16} />}
                    </Button>
                    <Button onClick={resetExercise} variant="outline" className="rounded-full" data-testid="reset-exercise-btn">
                      <RotateCcw size={16} />
                    </Button>
                  </div>
                </motion.div>
              )}

              {phase === "outro" && (
                <motion.div key="outro" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
                  <div className="bg-white border border-[#EBE3DF] rounded-2xl p-8 mb-8">
                    <p className="text-[#111111]/70 leading-relaxed text-base italic" data-testid="exercise-outro">{active.outro}</p>
                  </div>
                  <div className="flex gap-3 justify-center">
                    <Button onClick={() => { resetExercise(); setActive(null); }} variant="outline" className="rounded-full">
                      Alla övningar
                    </Button>
                    <Button onClick={resetExercise} className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full">
                      Gör om
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Exercise list
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <div className="pt-24 sm:pt-28 pb-8 sm:pb-20 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-14">
            <p className="text-sm uppercase tracking-[0.25em] text-[#A0655A] font-medium mb-4">Stanna upp</p>
            <h1 className="heading-font text-3xl sm:text-4xl font-medium text-[#111111] mb-3" data-testid="exercises-page-title">Guidade övningar</h1>
            <p className="text-[#111111]/50 text-base max-w-md mx-auto leading-relaxed">
              Korta övningar som hjälper dig att landa, släppa och bara vara.
            </p>
          </motion.div>

          <div className="space-y-4">
            {exercises.map((ex, idx) => (
              <motion.button
                key={ex.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * idx }}
                onClick={() => startExercise(ex.id)}
                className="w-full bg-white border border-[#EBE3DF] rounded-2xl p-6 text-left hover:shadow-lg transition-all group"
                data-testid={`exercise-card-${ex.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-[#A0655A] font-medium mb-1.5">{ex.subtitle}</p>
                    <h2 className="heading-font text-xl font-medium text-[#111111] mb-1">{ex.title}</h2>
                    <p className="text-sm text-[#111111]/40">{ex.duration_minutes} min</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-[#FBF5F2] flex items-center justify-center group-hover:bg-[#A0655A] transition-colors">
                    <Play className="text-[#A0655A] group-hover:text-white transition-colors" size={18} />
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ExercisesPage;
